import React from 'react';
import styles from './CollaboratedReports.module.scss';

import Loader from '../../../Components/Loader/Loader';
import { ReportList } from '../../../Components/ReportList/ReportList';
import { ReportValue, ReportResponse } from '../../../Models';
import {
  getCollaboratedReports,
  getReportsByUrl,
  ReportListQuerySortBy
} from '../../../Services/ReportService';
import { NoReports } from '../../../Components/NoReports/NoReports';
import { useHistory } from 'react-router-dom';
import { OnlineStatusContext } from '../../../Context/OnlineStatusContext';
import { SFSpinner, SFText } from 'sfui';
import { handleError } from '../../../Helpers/errors';
import { useIsMounted } from 'ui-smartforce-settings';
import { UPDATE_REPORT_LIST } from '../../../Constants/Events';
import { useDispatchMainAlert } from '../../../Hooks';
import { ReportSortGroupByButton } from '../../../Components/ReportList/ReportSortGroupByButton/ReportSortGroupByButton';
import { ReportListGroupedBy } from '../../../Components/ReportList/ReportSortGroupByButton/ReportSortGroupByButtonMenu/ReportSortGroupByButtonMenu';
import { WorkspaceView } from '../../../Components/WorkspaceView/WorkspaceView';

export const CollaboratedReports = (): React.ReactElement<{}> => {
  const history = useHistory();

  const { isOnline } = React.useContext(OnlineStatusContext);
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [isFetchingMoreReports, setIsFetchingMoreReports] =
    React.useState<boolean>(true);
  const [reports, setReports] = React.useState<ReportValue[]>([]);
  const [groupedBy, setGroupedBy] =
    React.useState<ReportListGroupedBy>('report');
  const [sortBy, setSortBy] = React.useState<ReportListQuerySortBy>('newest');

  const refNextUrl = React.useRef<string | undefined>();
  const isMounted = useIsMounted();

  useDispatchMainAlert();

  React.useEffect(() => {
    const getReports = async () => {
      try {
        setIsLoading(true);
        const response: ReportResponse = await getCollaboratedReports();

        if (isMounted()) {
          refNextUrl.current = response.links.next;
          setReports(response.reports);
          setIsLoading(false);
          setIsFetchingMoreReports(false);
        }
      } catch (e) {
        console.error('CollaboratedReports::getReports', e);
        handleError(e, history);
      }
    };

    if (isOnline) getReports();

    document.addEventListener(UPDATE_REPORT_LIST, getReports);

    return () => {
      document.removeEventListener(UPDATE_REPORT_LIST, getReports);
    };
  }, [history, isOnline, isMounted]);

  const onScrollBottom = async () => {
    if (refNextUrl.current && !isFetchingMoreReports) {
      setIsFetchingMoreReports(true);

      try {
        const response = await getReportsByUrl(refNextUrl.current);

        if (isMounted()) {
          setReports([...reports, ...response.reports]);
          setIsFetchingMoreReports(false);
          refNextUrl.current = response.links.next;
        }
      } catch (e) {
        console.error('CollaboratedReports::getMoreReports', e);
        handleError(e, history);
      }
    }
  };

  const onSortByChange = async (sortBy: ReportListQuerySortBy) => {
    try {
      setSortBy(sortBy);
      setIsLoading(true);
      const response: ReportResponse = await getCollaboratedReports(sortBy);

      if (isMounted()) {
        refNextUrl.current = response.links.next;
        setReports(response.reports);
        setIsLoading(false);
        setIsFetchingMoreReports(false);
      }
    } catch (e) {
      console.error('CollaboratedReports::onSortByChange', e);
      setIsLoading(false);
      handleError(e, history);
    }
  };

  const hasReports: boolean = reports.length > 0;

  return (
    <div className={styles.collaboratedReports}>
      {isLoading && <Loader />}
      {!isLoading && !hasReports && <NoReports />}

      {!isLoading && hasReports && (
        <WorkspaceView
          onScrollBottom={onScrollBottom}
          sticky={
            <div className={styles.sticky}>
              <SFText type="component-2" sfColor="neutral">
                ALL REPORTS
              </SFText>

              <ReportSortGroupByButton
                groupedBy={groupedBy}
                sortBy={sortBy}
                onGroupedByChange={(g: ReportListGroupedBy) => setGroupedBy(g)}
                onSortByChange={onSortByChange}
              />
            </div>
          }
        >
          <div className={styles.reports}>
            <ReportList groupedBy={groupedBy} reports={reports} />

            {isFetchingMoreReports && (
              <div className={styles.moreReportsSpinner}>
                <SFSpinner className={styles.spinner} size={40} />
              </div>
            )}
          </div>
        </WorkspaceView>
      )}
    </div>
  );
};
